import { Link, useParams, useSearchParams } from "react-router-dom";
import { ExternalLink, CheckCircle2, XCircle, Award, ArrowRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import Markdown from "@/components/site/Markdown";
import { SoftwareLogo } from "@/components/site/SoftwareCard";
import { PageHeader, Loading, Empty, AffiliateNotice } from "@/components/site/Ui";
import { useApi, formatDate, pricingLabel, outbound } from "@/lib/api";
import { useCompare } from "@/lib/compare";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function Table({ cols, rows, testId }) {
  return (
    <div className="overflow-x-auto card" data-testid={testId}>
      <table className="w-full text-sm min-w-[560px]">
        <thead>
          <tr className="bg-[#F1F4F6]">
            <th className="text-left p-4 font-semibold text-[#64728A] text-xs uppercase tracking-wider w-48">Feature</th>
            {cols.map((s) => (
              <th key={s.slug} className="text-left p-4">
                <Link to={`/software/${s.slug}`} className="flex items-center gap-2.5 font-semibold text-[#17243A] hover:text-[#3F5E7A]"><SoftwareLogo s={s} size="h-9 w-9" />{s.name}</Link>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, vals]) => (
            <tr key={label} className="border-t border-[#E7EDEF] align-top">
              <th scope="row" className="text-left p-4 font-medium text-[#17243A]">{label}</th>
              {vals.map((v, i) => <td key={i} className="p-4 text-[#3A4A5E]">{v || "—"}</td>)}
            </tr>
          ))}
          <tr className="border-t border-[#E7EDEF]">
            <th scope="row" className="text-left p-4 font-medium text-[#17243A]">Official website</th>
            {cols.map((s) => <td key={s.slug} className="p-4"><a {...outbound(s)} className="inline-flex items-center gap-1 text-[#3F5E7A] font-semibold hover:underline" data-testid={`compare-visit-${s.slug}`}>Visit <ExternalLink className="h-3.5 w-3.5" /></a></td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const bullet = (arr) => (arr?.length ? <ul className="space-y-1">{arr.map((x) => <li key={x}>• {x}</li>)}</ul> : null);
const pricingCell = (s) => (s.pricing_verified_at && s.pricing_summary ? <>{s.pricing_summary}<span className="block text-xs text-[#0F766E] mt-1">Verified {formatDate(s.pricing_verified_at)}</span></> : <>{pricingLabel(s.pricing_model)}<span className="block text-xs text-amber-700 mt-1">Details not yet verified</span></>);

export function ComparisonsPage() {
  const { data, loading } = useApi("/comparisons");
  return (
    <>
      <Seo title="Software Comparisons" description="Side-by-side software comparisons covering features, pricing, ideal customers, strengths and key differences." />
      <PageHeader eyebrow="Comparisons" title="Compare solutions side by side" testId="comparisons-page-header">
        Editorial comparisons of popular tools — or build your own from the <Link to="/software" className="text-[#3F5E7A] underline">directory</Link>.
      </PageHeader>
      <div className="container-x py-12">
        {loading ? <Loading /> : !data?.length ? <Empty title="No comparisons published yet" /> : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.map((c) => (
              <Link key={c.slug} to={`/comparisons/${c.slug}`} className="card card-hover p-6 group" data-testid={`comparison-card-${c.slug}`}>
                <div className="flex -space-x-2">{c.software_docs.map((s) => <SoftwareLogo key={s.slug} s={s} size="h-11 w-11" />)}</div>
                <h2 className="mt-5 text-lg font-bold group-hover:text-[#3F5E7A] transition-colors">{c.title}</h2>
                <p className="mt-2 text-sm line-clamp-3">{c.intro}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#3F5E7A]">Read comparison <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export function ComparisonDetail() {
  const { slug } = useParams();
  const { data: c, loading, error } = useApi(`/comparisons/${slug}`);
  if (loading) return <Loading />;
  if (error) return <div className="container-x py-20"><Empty title="Comparison not found"><Link to="/comparisons" className="text-[#3F5E7A] underline">All comparisons</Link></Empty></div>;
  const sw = c.software_docs;
  const item = (s) => c.items?.find((i) => i.slug === s.slug) || {};
  const hasAff = sw.some((s) => s.affiliate_url);
  const rows = [
    ...c.feature_rows.map((r) => [r.feature, sw.map((s) => r.values[c.software.indexOf(s.slug)])]),
    ["Pricing model", sw.map((s) => pricingLabel(s.pricing_model))],
  ];
  return (
    <article data-testid="comparison-page">
      <Seo title={c.seo_title || c.title} description={c.seo_description || c.intro?.slice(0, 160)} type="article"
        jsonLd={{ "@context": "https://schema.org", "@type": "Article", headline: c.title, datePublished: c.published_at, dateModified: c.updated_at, author: { "@type": "Organization", name: c.author } }} />
      <PageHeader eyebrow={<Link to="/comparisons" className="hover:underline">Comparisons</Link>} title={c.title} testId="comparison-header">
        <p className="text-xs text-[#64728A]">By {c.author} · Published {formatDate(c.published_at)} · Updated {formatDate(c.updated_at)}</p>
      </PageHeader>
      <div className="container-x py-12 space-y-14">
        {hasAff && <AffiliateNotice />}
        {c.intro && <Markdown className="max-w-3xl text-lg">{c.intro}</Markdown>}
        <section><h2 className="text-2xl font-bold mb-5">Feature comparison</h2><Table cols={sw} rows={rows} testId="comparison-feature-table" /></section>
        <section data-testid="comparison-pricing">
          <h2 className="text-2xl font-bold mb-5">Pricing comparison</h2>
          <div className={`grid gap-5 md:grid-cols-2 ${sw.length > 2 ? "lg:grid-cols-4" : ""}`}>{sw.map((s) => (
            <div key={s.slug} className="card p-5"><p className="font-semibold text-[#17243A]">{s.name}</p><div className="mt-2 text-sm">{item(s).pricing || pricingCell(s)}</div></div>
          ))}</div>
        </section>
        <section data-testid="comparison-profiles">
          <h2 className="text-2xl font-bold mb-5">Ideal customer, strengths & weaknesses</h2>
          <div className="grid gap-5 md:grid-cols-2">{sw.map((s) => { const it = item(s); return (
            <div key={s.slug} className="card p-6">
              <div className="flex items-center gap-3"><SoftwareLogo s={s} size="h-10 w-10" /><h3 className="text-lg font-semibold">{s.name}</h3></div>
              {it.ideal_customer && <p className="mt-4 text-sm"><strong className="text-[#17243A]">Ideal for:</strong> {it.ideal_customer}</p>}
              {it.strengths?.length > 0 && <ul className="mt-4 space-y-2 text-sm">{it.strengths.map((x) => <li key={x} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#0F766E] mt-0.5 shrink-0" />{x}</li>)}</ul>}
              {it.weaknesses?.length > 0 && <ul className="mt-3 space-y-2 text-sm">{it.weaknesses.map((x) => <li key={x} className="flex gap-2"><XCircle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />{x}</li>)}</ul>}
              <Link to={`/software/${s.slug}`} className="mt-5 inline-flex text-sm font-semibold text-[#3F5E7A] hover:underline">Full {s.name} profile →</Link>
            </div>); })}</div>
        </section>
        {c.key_differences?.length > 0 && <section data-testid="comparison-differences"><h2 className="text-2xl font-bold mb-4">Key differences</h2>
          <ul className="space-y-3 max-w-3xl">{c.key_differences.map((d) => <li key={d} className="flex gap-3"><ArrowRight className="h-5 w-5 text-[#3F5E7A] shrink-0 mt-0.5" />{d}</li>)}</ul></section>}
        {c.faqs?.length > 0 && <section data-testid="comparison-faq" className="max-w-3xl"><h2 className="text-2xl font-bold mb-4">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="card px-5">
            {c.faqs.map((f, i) => <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left font-semibold text-[#17243A]">{f.q}</AccordionTrigger><AccordionContent className="text-[#526174]">{f.a}</AccordionContent></AccordionItem>)}
          </Accordion></section>}
        {c.recommendation && <section data-testid="comparison-recommendation" className="rounded-2xl bg-[#17243A] p-8 md:p-10 text-slate-200 max-w-4xl">
          <p className="flex items-center gap-2 eyebrow !text-[#8FD3C8]"><Award className="h-4 w-4" />Editorial recommendation</p>
          <div className="mt-3 [&_*]:!text-slate-100"><Markdown>{c.recommendation}</Markdown></div>
        </section>}
      </div>
    </article>
  );
}

export function CompareBuilder() {
  const [params] = useSearchParams();
  const slugs = params.get("items") || "";
  const { clear } = useCompare();
  const { data, loading } = useApi(slugs ? `/software/batch?slugs=${encodeURIComponent(slugs)}` : null);
  return (
    <>
      <Seo title="Compare Software" noindex />
      <PageHeader eyebrow="Your comparison" title="Side-by-side comparison" testId="compare-builder-header">
        Based on the factual profile data we hold for each tool. Confirm details on each official website.
      </PageHeader>
      <div className="container-x py-12">
        {!slugs ? <Empty title="No tools selected">Add tools using the Compare buttons in the <Link to="/software" className="text-[#3F5E7A] underline">directory</Link>.</Empty>
          : loading ? <Loading /> : data?.length < 2 ? <Empty title="Select at least two tools"><Link to="/software" className="text-[#3F5E7A] underline">Back to directory</Link></Empty> : (
          <>
            {data.some((s) => s.affiliate_url) && <div className="mb-6"><AffiliateNotice /></div>}
            <Table cols={data} testId="compare-builder-table" rows={[
              ["Summary", data.map((s) => s.short_description)],
              ["Pricing", data.map(pricingCell)],
              ["Key features", data.map((s) => bullet(s.key_features))],
              ["Best for", data.map((s) => bullet(s.best_use_cases) || s.use_cases?.join(", "))],
              ["Pros", data.map((s) => bullet(s.pros))],
              ["Cons", data.map((s) => bullet(s.cons))],
              ["Limitations", data.map((s) => bullet(s.limitations))],
            ]} />
            <div className="mt-6 flex gap-3"><Link to="/software" className="btn-secondary" data-testid="compare-builder-add-more">Change selection</Link><button onClick={clear} className="btn-ghost" data-testid="compare-builder-clear">Clear selection</button></div>
          </>
        )}
      </div>
    </>
  );
}
